import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DocumentReference,
  DocumentSnapshot,
  Query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  onSnapshot,
  DocumentData,
  SnapshotListenOptions,
  SnapshotOptions,
} from 'firebase/firestore';

const DEFAULT_OPTIONS: SnapshotListenOptions = { includeMetadataChanges: false };

/**
 * Preferencia de origen para `collectionData`:
 * - `'default'`: el listener emite cache-first y luego servidor (comportamiento nativo de `onSnapshot`).
 * - `'server-first'`: descarta las emisiones que vienen de cache hasta el 1er snapshot de servidor,
 *   evitando el flicker de mostrar datos parciales/cacheados antes de la respuesta fresca. Un listener
 *   NO puede forzar "solo servidor" (Firestore no lo soporta), por eso hay un fallback por timeout que
 *   emite lo último cacheado si el servidor nunca responde (offline).
 */
export type CollectionDataSource = 'default' | 'server-first';

const DEFAULT_SERVER_FIRST_TIMEOUT_MS = 3000;

export function fromRef<T = DocumentData>(
  ref: DocumentReference<T>,
  options?: SnapshotListenOptions,
): Observable<DocumentSnapshot<T>>;
export function fromRef<T = DocumentData>(
  ref: Query<T>,
  options?: SnapshotListenOptions,
): Observable<QuerySnapshot<T>>;
export function fromRef<T = DocumentData>(
  ref: DocumentReference<T> | Query<T>,
  options: SnapshotListenOptions = DEFAULT_OPTIONS,
): Observable<DocumentSnapshot<T> | QuerySnapshot<T>> {
  return new Observable(subscriber => {
    const unsubscribe = onSnapshot(
      ref as any,
      options,
      {
        next: subscriber.next.bind(subscriber),
        error: subscriber.error.bind(subscriber),
        complete: subscriber.complete.bind(subscriber),
      },
    );
    return { unsubscribe };
  });
}

export function snapToData<T = DocumentData>(
  snapshot: DocumentSnapshot<T>,
  options: { idField?: string } & SnapshotOptions = {},
): T | undefined {
  const { idField, ...snapshotOptions } = options;
  const data = snapshot.data(snapshotOptions);

  if (!snapshot.exists() || typeof data !== 'object' || data === null || !idField) {
    return data;
  }

  return {
    ...data,
    [idField]: snapshot.id,
  };
}

function doc<T = DocumentData>(ref: DocumentReference<T>): Observable<DocumentSnapshot<T>> {
  return fromRef(ref, { includeMetadataChanges: true });
}

export function docData<T = DocumentData>(
  ref: DocumentReference<T>,
  options: { idField?: string } & SnapshotOptions = {},
): Observable<T | undefined> {
  return doc(ref).pipe(map(snap => snapToData(snap, options)));
}

function collectionSnapshot<T = DocumentData>(query: Query<T>): Observable<QuerySnapshot<T>> {
  return fromRef(query, { includeMetadataChanges: true });
}

function collection<T = DocumentData>(query: Query<T>): Observable<QueryDocumentSnapshot<T>[]> {
  return collectionSnapshot(query).pipe(map(snapshot => snapshot.docs));
}

/**
 * Descarta las emisiones cacheadas hasta el 1er snapshot de servidor (o hasta que vence `timeoutMs`,
 * en cuyo caso emite lo último cacheado). Una vez abierta la compuerta, pasa todo sin filtrar.
 */
function serverFirst<T>(
  timeoutMs: number,
): (source: Observable<QuerySnapshot<T>>) => Observable<QuerySnapshot<T>> {
  return source =>
    new Observable<QuerySnapshot<T>>(subscriber => {
      let gateOpen = false;
      let latestCached: QuerySnapshot<T> | undefined;

      const fallback = setTimeout(() => {
        if (gateOpen) {
          return;
        }
        gateOpen = true;
        if (latestCached) {
          subscriber.next(latestCached);
        }
      }, timeoutMs);

      const subscription = source.subscribe({
        next: snapshot => {
          if (gateOpen) {
            subscriber.next(snapshot);
            return;
          }
          if (!snapshot.metadata.fromCache) {
            gateOpen = true;
            clearTimeout(fallback);
            subscriber.next(snapshot);
            return;
          }
          latestCached = snapshot;
        },
        error: error => {
          clearTimeout(fallback);
          subscriber.error(error);
        },
        complete: () => {
          clearTimeout(fallback);
          subscriber.complete();
        },
      });

      return () => {
        clearTimeout(fallback);
        subscription.unsubscribe();
      };
    });
}

export function collectionData<T = DocumentData>(
  query: Query<T>,
  options: {
    idField?: string;
    source?: CollectionDataSource;
    serverFirstTimeoutMs?: number;
  } & SnapshotOptions = {},
): Observable<T[]> {
  const {
    idField,
    source = 'default',
    serverFirstTimeoutMs = DEFAULT_SERVER_FIRST_TIMEOUT_MS,
    ...snapshotOptions
  } = options;

  const querySnapshots = collectionSnapshot(query);
  const gated =
    source === 'server-first'
      ? querySnapshots.pipe(serverFirst(serverFirstTimeoutMs))
      : querySnapshots;

  return gated.pipe(
    map(snapshot => snapshot.docs.map(snap => snapToData(snap, { idField, ...snapshotOptions })!)),
  );
}

export function collectionSnapshots<T = DocumentData>(
  query: Query<T>,
): Observable<QueryDocumentSnapshot<T>[]> {
  return collection(query);
}
